/** El razonamiento del turno: expandido mientras trabaja, colapsado a una línea al terminar.
 *
 * Etiqueta y forma tomadas de la referencia del cliente (HPE AI Agent): un bloque
 * «Razonamiento · N pasos» con chevron, que agrupa el plan y las herramientas en vez de dejar
 * una lista suelta encima de la respuesta. Antes los pasos se quedaban abiertos para siempre y
 * empujaban la respuesta fuera de la pantalla.
 */
import { useState } from "react";
import type { Idioma, Turno } from "../lib/tipos";
import { ms } from "../lib/formato";
import { etiquetaDominio, t } from "../lib/i18n";

export function ProgresoTools({ turno, idioma }: { turno: Turno; idioma: Idioma }) {
  const txt = t(idioma);
  const enVivo = turno.enVuelo;
  // Abierto mientras trabaja; cerrado en cuanto termina. Los pasos cumplidos son historia, y
  // dejarlos abiertos empujaba la respuesta —lo que importa— fuera de la pantalla.
  const [abierto, setAbierto] = useState(false);
  if (!turno.progreso.length && !enVivo) return null;

  const listos = turno.progreso.filter((p) => p.estado === "listo").length;
  const errores = turno.progreso.filter((p) => p.estado === "error").length;
  const visible = enVivo || abierto;
  const n = turno.progreso.length;

  return (
    <section className={`razonamiento${enVivo ? " vivo" : ""}`}>
      <button className="razonamiento-cabeza" onClick={() => !enVivo && setAbierto((v) => !v)}
              aria-expanded={visible} disabled={enVivo}>
        <span className="razonamiento-icono" aria-hidden="true">{enVivo ? "◌" : "✓"}</span>
        <strong>{txt.razonamiento}</strong>
        <span className="razonamiento-cuenta">
          · {enVivo ? `${listos}/${n}` : n} {n === 1 ? txt.paso : txt.pasos}
        </span>
        {errores > 0 && <span className="chip aviso">{errores}</span>}
        {!enVivo && turno.meta && <em className="razonamiento-ms">{ms(turno.meta.latencia_ms)}</em>}
        {!enVivo && <span className={`chevron${visible ? " abierto" : ""}`} aria-hidden="true">⌄</span>}
      </button>

      {visible && (
        <ol className="pasos" aria-live={enVivo ? "polite" : "off"}>
          {turno.progreso.map((p) => (
            <li key={p.id} className={`paso ${p.estado}`}>
              <span className="paso-marca" aria-hidden="true">
                {p.estado === "listo" ? "✓" : p.estado === "error" ? "!" : "·"}
              </span>
              <span className="paso-texto">{p.etiqueta || etiquetaDominio(p.tool, idioma) || txt.pensando}</span>
              <code className="paso-tool">{p.tool}</code>
              {p.mensaje && <em className="paso-error">{p.mensaje}</em>}
            </li>
          ))}
          {enVivo && !turno.respuesta_md && (
            <li className="paso corriendo">
              <span className="paso-marca" aria-hidden="true">·</span>
              <span className="paso-texto">{turno.revisando ? txt.revisando : txt.pensando}</span>
            </li>
          )}
        </ol>
      )}
    </section>
  );
}
